<script lang="ts" setup>
    import { onMounted, reactive } from 'vue';
    import '../../../css/choice.css';
    import * as choiceApi from '../../lib/ChoiceApi';
    import { IFishboneSubType, IWireframe, IWireframeConfiguration, WireframeSubType } from '../../lib/Models/Fishbones';
    import TextInput from '../../components/TextInput.vue';
    import ActionButtonV2 from '../../components/ActionButtonV2.vue';
    import { ActionButtonStyle } from '../../lib/Types/Types';
    import { DisplayStyle, ButtonDisplaySize, ButtonDisplayStyle, IconTypes } from '../../Enums/UiEnums';
    import { confirmDialog, ModalStyles, popAlert } from '../../lib/Notification';
    import Icon from '../../components/Icon.vue';
    import Checkbox from '../../components/Inputs/Checkbox.vue';
    import ChoiceButton from '../../components/Buttons/ChoiceButton.vue';
    import { combinePaths } from '../../lib/ChoiceApi';

    let pageData = reactive<{
        fbId: number,
        wireframes?: IWireframe[],
        wireframeConfigs?: IWireframeConfiguration[],
        selectedWireframe?: IWireframe,
        fishboneItems?: IFishboneSubType[]
    }>({
        fbId: -1,
        wireframes: undefined,
        wireframeConfigs: undefined,
        selectedWireframe: undefined,
        fishboneItems: undefined
    });

    let editState = reactive<{
        wireframeNames: string[],
        imageNames: string[],
        wireframeToCreateName?: string,
        wireframeToCreateImage?: string,
        wireframeConfigEdit?: IWireframe,
        wfConfigLabelPayload: IWireframeConfiguration[],
        wfConfigResultPayload: IWireframeConfiguration[]
    }>({
        wireframeNames: [],
        imageNames: [],
        wireframeToCreateName: undefined,
        wireframeToCreateImage: undefined,
        wireframeConfigEdit: undefined,
        wfConfigLabelPayload: [],
        wfConfigResultPayload: []
    });

    onMounted(async () => {
        const params = new URLSearchParams(window.location.search);
        pageData.fbId = Number(params.get('fbId'));

        await Promise.all([
            getFishboneItems(),
            getWireframeData()
        ]);
    });

    async function getFishboneItems() {
        const fishboneItems = await choiceApi.fishbones.getFishboneSubTypes();
        if (fishboneItems) {
            pageData.fishboneItems = fishboneItems;
        }
    }

    async function getWireframeData() {

        const [wireframes, wireframeConfigs] = await Promise.all([
            choiceApi.fishbones.getWireframes(pageData.fbId),
            choiceApi.fishbones.getWireframeConfigs()
        ]);

        if (!wireframes || !wireframeConfigs) {
            popAlert("Failed to get wireframe data", "Wireframe Setup", ModalStyles.info);
            return;
        }

        pageData.wireframes = wireframes;
        pageData.wireframeConfigs = wireframeConfigs;

        pageData.wireframes?.forEach(
            (wireframe) => { 
                editState.wireframeNames.push(wireframe.wireframeName);
                editState.imageNames.push(wireframe.imageName);
            }
        )

        initializeWireframeConfigPayloads();
        
        // auto-select the wireframe if it's the only one for the given fbId
        if (pageData.wireframes?.length == 1) {
            pageData.selectedWireframe = pageData.wireframes[0];
        }
    }

    function initializeWireframeConfigPayloads() {
        editState.wfConfigLabelPayload = [];
        editState.wfConfigResultPayload = [];

        const wireframe_id = pageData.selectedWireframe?.wireframeId ?? -1;
        pageData.fishboneItems?.filter(fbItem => fbItem.fbId == pageData.fbId).forEach(
            (fbItem) => {
                const labelWireframeConfig = pageData.wireframeConfigs?.find(config => config.fbSubId == fbItem.fbSubId && config.wireframeId == wireframe_id && config.fbSubType == WireframeSubType.Label);
                const resultWireframeConfig = pageData.wireframeConfigs?.find(config => config.fbSubId == fbItem.fbSubId && config.wireframeId == wireframe_id && config.fbSubType == WireframeSubType.Result);
                editState.wfConfigLabelPayload?.push(
                    {
                        wireframeId: wireframe_id,
                        fbId: pageData.fbId,
                        fbSubId: fbItem.fbSubId,
                        fbSubType: WireframeSubType.Label,
                        fbLeft: labelWireframeConfig?.fbLeft ?? 0,
                        fbTop: labelWireframeConfig?.fbTop ?? 0,
                        fbHeight: labelWireframeConfig?.fbHeight ?? 0,
                        fbWidth: labelWireframeConfig?.fbWidth ?? 0
                    }
                )
                editState.wfConfigResultPayload?.push(
                    {
                        wireframeId: wireframe_id,
                        fbId: pageData.fbId,
                        fbSubId: fbItem.fbSubId,
                        fbSubType: WireframeSubType.Result,
                        fbLeft: resultWireframeConfig?.fbLeft ?? 0,
                        fbTop: resultWireframeConfig?.fbTop ?? 0,
                        fbHeight: resultWireframeConfig?.fbHeight ?? 0,
                        fbWidth: resultWireframeConfig?.fbWidth ?? 0
                    }
                )
            }
        )
    }

    function getFbItemLabel(config: IWireframeConfiguration) {
        return pageData.fishboneItems?.find(fbItem => fbItem.fbSubId == config.fbSubId)?.label;
    }

    async function setupWireframe() {
        editState.wireframeToCreateName = undefined;
        editState.wireframeToCreateImage = undefined;
        
        if (!pageData.selectedWireframe) {
            popAlert("A wireframe must be selected.", "Wireframe Setup", ModalStyles.info);
            return;
        }

        editState.wireframeConfigEdit = pageData.selectedWireframe;
        await getWireframeData();
    }

    async function createWireframe() {
        if (!editState.wireframeToCreateName || !editState.wireframeToCreateImage) {
            popAlert("Wireframe name and image path must be provided.", "Wireframe Setup", ModalStyles.info);
            return;
        }
        const result = await choiceApi.fishbones.CreateWireframe({
                fbId: pageData.fbId,
                wireframeName: editState.wireframeToCreateName,
                imageName: editState.wireframeToCreateImage
            }
        )

        if (result) {
            popAlert(result, "Wireframe Create", ModalStyles.info);
            return;
        }

        updatePageData();
    }

    async function updateWireframe() {
        editState.wireframeToCreateName = undefined;
        editState.wireframeToCreateImage = undefined;

        if (!pageData.selectedWireframe) {
            popAlert("A wireframe must be selected.", "Wireframe Update", ModalStyles.info);
            return;
        }
        const indexOfWireframeToUpdate = pageData.wireframes?.indexOf(pageData.selectedWireframe) ?? -1;
        if (indexOfWireframeToUpdate >= 0) {
            const wireframeUpdatePayload: IWireframe = {
                wireframeId: pageData.selectedWireframe.wireframeId,
                fbId: pageData.selectedWireframe.fbId,
                wireframeName: editState.wireframeNames[indexOfWireframeToUpdate],
                imageName: editState.imageNames[indexOfWireframeToUpdate],
                disabled: pageData.selectedWireframe.disabled
            }
            const result = await choiceApi.fishbones.updateWireframe(wireframeUpdatePayload);
            if (result) { // error
                popAlert(result, "Wireframe Save", ModalStyles.warning);
            }
            updatePageData();
        }
    }

    async function updateWireframeConfig() {
        editState.wireframeToCreateName = undefined;
        editState.wireframeToCreateImage = undefined;

        const wireframeConfigPayload = editState.wfConfigLabelPayload.concat(editState.wfConfigResultPayload);
        if (wireframeConfigPayload.find(config => isNaN(config.fbLeft) || isNaN(config.fbTop) || isNaN(config.fbHeight) || isNaN(config.fbWidth))) {
            popAlert("Wireframe configuration value must be numeric.", "Wireframe Configuration", ModalStyles.warning);
            return;
        }

        const result = await choiceApi.fishbones.createOrUpdateWireframeConfig(wireframeConfigPayload);
        if (result) { // error
            popAlert(result, "Wireframe Update", ModalStyles.warning);
        }
    }

    function updatePageData() {
        resetEditState();
        getWireframeData();
    }

    function resetEditState() {
        editState.wireframeNames = [];
        editState.imageNames = [];
        editState.wireframeToCreateName = undefined;
        editState.wireframeToCreateImage = undefined;
    }

    function newWireframe() {
        pageData.selectedWireframe = undefined;
        editState.wireframeToCreateName = "";
        editState.wireframeToCreateImage = "";
    }

    async function displayDeleteWarning() {
        const result = await confirmDialog(
            "Are you sure?",
            { 
                titleHTML: "Remove Wireframe",
                ConfirmText: "Remove",
                RejectText: "Cancel",
                modalStyle: ModalStyles.danger
            }
        );
        if (result && pageData.selectedWireframe) {
            const deleteResult = await choiceApi.fishbones.deleteWireframe(pageData.selectedWireframe);
            if (deleteResult) { // error
                popAlert(deleteResult, "Wireframe Delete", ModalStyles.info);
                return;
            }
            pageData.selectedWireframe = undefined;
            updatePageData();
        }
    }

    function computedStyleLabel(config: IWireframeConfiguration) {
        return {
            position: 'absolute',
            left: `${config.fbLeft}px`,
            top: `${config.fbTop}px`,
            height: `${config.fbHeight}px`,
            width: `${config.fbWidth}px`,
            color: 'lightgrey',
            textAlign: 'center',
            fontSize: '8pt'
        };
    }

    function computedStyleResult(config: IWireframeConfiguration) {
        return {
            position: 'absolute',
            left: `${config.fbLeft}px`,
            top: `${config.fbTop}px`,
            height: `${config.fbHeight}px`,
            width: `${config.fbWidth}px`,
            color: 'white',
            textAlign: 'center',
            fontWeight: 'bold'
        };
    }

    function toggleWireframeDisplay() {
        if (pageData.selectedWireframe) {
            pageData.selectedWireframe.disabled = !pageData.selectedWireframe.disabled;
        }
    }

    function getWireframeImageUrl(imageName: string): string {
        return combinePaths(['/officeMd/images', imageName], false);
    }
    
    function close() {
        window.close();
    }
</script>

<style scoped>
    #wireframeSetup{
        background-color: #F4F4F4;
        width: 99.99vw;
        height: 100vh;
        margin-left: -12px;
        padding: 1rem;
    }
    #wireframeSetup *,
    #wfItemHeaders *,
    #wfConfigHeaders *,
    .wfInput > :first-child > :first-child {
        font-size: 96%;
    }
    #wireframeContainer{
        display: flex;
        background-color: white;
        width: 100%;
        height: 100%;
        border-radius: 0.5rem;
    }
    #wireframeItemContainer,
    #wireframeImgContainer{
        height: 100%;
    }
    #wireframeItemContainer{
        padding: 1rem;
        width: 65.5%;
        border-right: solid 0.2rem #F4F4F4;
    }
    #wireframeImgContainer {
        width: 47.5%;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
    }
    .wfHeaderWrapper {
        display: flex;
        margin-bottom: 1rem;
    }
    #wfItemHeaders {
        font-weight: bold;
        display: flex;
        width: 95%;
        margin-left: 2.5rem;
    }
    #wfConfigHeaders {
        font-weight: bold;
        display: flex;
        width: 100%;
        margin-left: 0.5rem;
    }
    .wfHeaderIcon {
        margin-top: 0.3rem;
        cursor: pointer;
    }
    .wfIconWrapper {
        margin: 0.25rem 0.45rem 0 0;
    }
    .wfItemContainer {
        display: flex;
        padding: 0.5rem;
        border-radius: 0.5rem;
        margin-bottom: 0.5rem;
    }
    .wfItemContainer:hover {
        color: black;
        background-color: #F3F8FF;
        cursor: pointer;
    }
    .wfItemContainerSelected {
        background-color: #F3F8FF;
    }
    .wfItemWrapper {
        display: flex;
        width: 98%;
    }
    .wfDeleteIcon {
        margin: 0.5rem 0.5rem 0 0;
        cursor: pointer;
    }
    #wfActionContainer {
        margin: 0 0 1rem 1rem;
        display: flex;
        gap: 0.5rem;
        width: 91%;
        justify-content: flex-end;
    }
    #wfActionContainer > button {
        width: 29%;
    }
    .wireframeName {
        width: 40%;
        margin-right: 0.5rem;
    }
    #wireframeDiagramContainer {
        height: 40%;
        margin: 1rem;
        width: 92%;
        position: relative;
    }
    .wireframeImgVisible {
        background-color: #396aa6;
    }
    .wireframeImageUrl {
        width: 60%;
    }
    #wireframeDisable {
        display: flex;
        position: absolute;
        top: 11.5rem;
        gap: 0.25rem;
        width: 100%;
        font-weight: 600;
    }
    #wireframeDisable > :first-child > :first-child {
        width: 1rem;
        height: 1rem;
        box-shadow: none;
        cursor: pointer;
    }
</style>

<template>
    <div id="wireframeSetup">
        <div id="wireframeContainer">
            <div id="wireframeItemContainer">
                <div>
                    <div v-if="!editState.wireframeConfigEdit" class="wfHeaderWrapper">
                        <div id="wfItemHeaders">
                            <div style="width: 42%;">Name</div>
                            <div>Image</div>
                        </div>
                        <Icon 
                            :icon="IconTypes.Add"
                            title="Create wireframe"
                            class="wfHeaderIcon"
                            @click="newWireframe"
                        />
                    </div>
                    <div v-if="editState.wireframeConfigEdit" class="wfHeaderWrapper">
                        <div id="wfConfigHeaders">
                            <div style="width: 19%;">Label</div>
                            <div style="width: 16%;">Type</div>
                            <div style="width: 14%;">Left</div>
                            <div style="width: 11%;">Top</div>
                            <div style="width: 16%;">Height</div>
                            <div style="width: 16%;">Width</div>
                        </div>
                    </div>
                </div>
                <div style="overflow: auto; max-height: 90%;">
                    <div 
                        v-if="!editState.wireframeConfigEdit && pageData.wireframes?.length" 
                        v-for="(item, index) in pageData.wireframes.filter((wireframe: IWireframe) => wireframe.fbId == pageData.fbId)"
                        :key="index"
                    >
                        <div 
                            class="wfItemContainer" 
                            :class="{ 'wfItemContainerSelected' : pageData.selectedWireframe?.wireframeId == item.wireframeId }" 
                            @click="() => { pageData.selectedWireframe = item }"
                        >
                            <div class="wfItemWrapper">
                                <div class="wfIconWrapper">
                                    <Icon 
                                        :icon="IconTypes.Delete"
                                        title="Delete wireframe"
                                        :displayStyle="DisplayStyle.Danger"
                                        class="wfDeleteIcon"
                                        @click="displayDeleteWarning"
                                    />
                                </div>
                                <div class="wireframeName wfInput">
                                    <TextInput
                                        v-model:textValue="editState.wireframeNames[index]"
                                    />
                                </div>
                                <div class="wireframeImageUrl wfInput">
                                    <TextInput
                                        v-model:textValue="editState.imageNames[index]"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-if="!editState.wireframeConfigEdit && editState.wireframeToCreateName != undefined"
                        @click="() => { pageData.selectedWireframe = undefined }"    
                    >
                        <div 
                            class="wfItemContainer" 
                        >
                            <div class="wfItemWrapper">
                                <div class="wireframeName">
                                    <TextInput
                                        v-model:textValue="editState.wireframeToCreateName"
                                    />
                                </div>
                                <div class="wireframeImageUrl">
                                    <TextInput
                                        v-model:textValue="editState.wireframeToCreateImage"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div 
                        v-if="editState.wireframeConfigEdit" 
                        v-for="(item, index) in editState.wfConfigLabelPayload"
                        :key="index"
                    >
                        <div class="wfItemContainer">
                            <div class="wfItemWrapper">
                                <div style="width: 20%; margin-top: 0.2rem;">{{ getFbItemLabel(item) }}</div>
                                <div style="width: 15%; margin: 0.2rem 0 0 0.2rem">Label</div>
                                <div class="wfInput" style="width: 13%; margin-left: 0.2rem;">
                                    <TextInput
                                        v-model:textValue="item.fbLeft"
                                    />
                                </div>
                                <div class="wfInput" style="width: 12%; margin-left: 0.2rem;">
                                    <TextInput
                                        v-model:textValue="item.fbTop"
                                    />
                                </div>
                                <div class="wfInput" style="width: 16%; margin-left: 0.2rem;">
                                    <TextInput
                                        v-model:textValue="item.fbHeight"
                                    />
                                </div>
                                <div class="wfInput" style="width: 16%; margin-left: 0.2rem;">
                                    <TextInput
                                        v-model:textValue="item.fbWidth"
                                    />
                                </div>
                            </div>
                        </div>
                        <div class="wfItemContainer">
                            <div class="wfItemWrapper">
                                <div style="width: 20%; margin-top: 0.2rem;">{{ getFbItemLabel(editState.wfConfigResultPayload[index]) }}</div>
                                <div style="width: 15%; margin: 0.2rem 0 0 0.2rem">Result</div>
                                <div class="wfInput" style="width: 13%; margin-left: 0.2rem;">
                                    <TextInput
                                        v-model:textValue="editState.wfConfigResultPayload[index].fbLeft"
                                    />
                                </div>
                                <div class="wfInput" style="width: 12%; margin-left: 0.2rem;">
                                    <TextInput
                                        v-model:textValue="editState.wfConfigResultPayload[index].fbTop"
                                    />
                                </div>
                                <div class="wfInput" style="width: 16%; margin-left: 0.2rem;">
                                    <TextInput
                                        v-model:textValue="editState.wfConfigResultPayload[index].fbHeight"
                                    />
                                </div>
                                <div class="wfInput" style="width: 16%; margin-left: 0.2rem;">
                                    <TextInput
                                        v-model:textValue="editState.wfConfigResultPayload[index].fbWidth"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div id="wireframeImgContainer">
                <div id="wireframeDiagramContainer" :class="{ 'wireframeImgVisible' : pageData.selectedWireframe}">
                    <img v-if="pageData.selectedWireframe" :src="getWireframeImageUrl(pageData.selectedWireframe.imageName)">
                    <div v-if="editState.wireframeConfigEdit" v-for="(item) in editState.wfConfigLabelPayload">
                        <span :style="computedStyleLabel(item)">{{ getFbItemLabel(item) }}</span>
                    </div>
                    <div v-if="editState.wireframeConfigEdit" v-for="(item) in editState.wfConfigResultPayload">
                        <span :style="computedStyleResult(item)">XXX</span>
                    </div>
                    <div id="wireframeDisable" v-if="pageData.selectedWireframe && !editState.wireframeConfigEdit">
                        <Checkbox 
                            :modelValue="pageData.selectedWireframe.disabled" 
                            @change="toggleWireframeDisplay"
                        />
                        <span>Disable Display</span>
                    </div>
                </div>
                <div id="wfActionContainer">
                    <ActionButtonV2
                        v-if="!editState.wireframeConfigEdit"
                        :action="ActionButtonStyle.Blank"
                        :display-style="DisplayStyle.Primary"
                        :performAction="setupWireframe"
                        :size="ButtonDisplaySize.Small"
                        text="Setup"
                    />
                    <ChoiceButton
                        v-if="editState.wireframeConfigEdit"
                        :action="ActionButtonStyle.ChevronLeft" 
                        :displayStyle="DisplayStyle.Primary"
                        @click="() => { editState.wireframeConfigEdit = undefined; }"
                        text="Back"
                    />
                    <ActionButtonV2
                        v-if="editState.wireframeConfigEdit"
                        :action="ActionButtonStyle.Save"
                        :display-style="ButtonDisplayStyle.OutlinePrimary"
                        :performAction="updateWireframeConfig"
                        :size="ButtonDisplaySize.Small"
                        text="Update"
                    />
                    <ActionButtonV2
                        v-if="!editState.wireframeToCreateName && !editState.wireframeConfigEdit"
                        :action="ActionButtonStyle.Save"
                        :display-style="ButtonDisplayStyle.OutlinePrimary"
                        :performAction="updateWireframe"
                        :size="ButtonDisplaySize.Small"
                        text="Update"
                    />
                    <ActionButtonV2
                        v-if="editState.wireframeToCreateName"
                        :action="ActionButtonStyle.Add"
                        :display-style="ButtonDisplayStyle.OutlinePrimary"
                        :performAction="createWireframe"
                        :size="ButtonDisplaySize.Small"
                        text="Create"
                    />
                    <ChoiceButton
                        :action="ActionButtonStyle.Close"
                        :display-style="DisplayStyle.Secondary"
                        @click="close"
                        :size="ButtonDisplaySize.Small"
                        text="Cancel"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

